import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Tab, Nav, Table } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { createDiscount } from "../../services/Discounts/discountsService";
import { FaPlus, FaTrash } from "react-icons/fa";
import ErrorModal from "../Modals/ErrorModal";
import SuccessModal from "../Modals/SuccessModal";
import InputField from "../Utils/InputField";
import ProductInput from "../Utils/ProductInput";
import DecimalInput from "../Utils/DecimalInput";
import { v4 as uuidv4 } from 'uuid';

const CreateDiscountModal = ({ show, handleClose, refreshDiscounts, onCreated }) => {
  const { register, handleSubmit, formState: { errors }, reset, setValue, control } = useForm();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [activeKey, setActiveKey] = useState("general");

  const [discounts, setDiscounts] = useState({
    ardido: [],
    avariado: [],
    impureza: [],
    quebrado: [],
    umidade: [],
    verde: [],
  });

  const [currentDiscount, setCurrentDiscount] = useState({
    value: "",
    discountValue: "",
    id: "",
    create: true,
  });

  const handleAddDiscount = (type) => {
    if (!currentDiscount.value || !currentDiscount.discountValue) {
      return;
    }

    const alreadyExists = discounts[type].some(
      (item) => parseFloat(item.value) === parseFloat(currentDiscount.value)
    );
  
    if (alreadyExists) {
      setErrorMessage("O valor já existe na lista de descontos.");
      setShowErrorModal(true);
      return;
    }

    setDiscounts((prev) => ({
      ...prev,
      [type]: [...prev[type], { ...currentDiscount, id: uuidv4(), create: true }],
    }));
    setCurrentDiscount({ value: "", discountValue: "" });
  };

  const handleRemoveDiscount = (type, id) => {
    setDiscounts((prev) => ({
      ...prev,
      [type]: prev[type].filter((item) => item.id !== id),
    }));
  };

  const onSubmit = async (data) => {
    const hasDiscounts = Object.values(discounts).some((category) => category.length > 0);

    if (!hasDiscounts) {
      setErrorMessage("É necessário adicionar pelo menos um desconto antes de salvar.");
      setShowErrorModal(true);
      return;
    }

    try {
      const finalData = {
        productId: data.productId.id,
        description: data.description,
        ...discounts,
      };

      const created = await createDiscount(finalData);

      if (onCreated) {
        onCreated({
          id: created.uuid,
          description: created.name,
        });
      } else {
        setSuccessMessage("Tabela de descontos criada com sucesso!");
        setShowSuccessModal(true);
      }

      reset();
    } catch (error) {
      setErrorMessage(error.message || "Erro ao criar tabela de descontos.");
      setShowErrorModal(true);
    }
  };

  const handleCloseErrorModal = () => setShowErrorModal(false);
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    refreshDiscounts();
    handleClose();
  };

  useEffect(() => {
    if (show) {
      reset();
    }
  }, [show, reset]);

  const renderDiscountTab = (type) => (
    <>
      <Row className="align-items-center mb-3">
        <Col md={3}>
          <DecimalInput
            label="Valor"
            value={currentDiscount.value}
            onChange={(value) => setCurrentDiscount((prev) => ({ ...prev, value }))}
          />
        </Col>
        <Col md={3}>
          <DecimalInput
            label="Desconto"
            value={currentDiscount.discountValue}
            onChange={(discountValue) => setCurrentDiscount((prev) => ({ ...prev, discountValue }))}
          />
        </Col>
        <Col md={1} className="text-end d-flex align-items-center justify-content-end">
          <Button
            className="button-custom mt-2"
            onClick={() => handleAddDiscount(type)}
            title="Adicionar"
          >
            <FaPlus />
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Valor</th>
                <th>Desconto</th>
                <th style={{ width: "50px" }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {discounts[type].map((item) => (
                <tr key={item.id}>
                  <td>{item.value}</td>
                  <td>{item.discountValue}</td>
                  <td className="text-center">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRemoveDiscount(type, item.id)}
                      title="Remover"
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
    </>
  );

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Cadastrar Tabela de Descontos</Modal.Title>
        </Modal.Header>
        <Form
            onSubmit={(e) => {
              e.stopPropagation();
              handleSubmit(onSubmit)(e);
            }}
          >
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <Tab.Container activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="general">Informações Gerais</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="ardido">Ardido</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="avariado">Avariado</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="impureza">Impureza</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="quebrado">Quebrado</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="umidade">Umidade</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="verde">Verde</Nav.Link>
                </Nav.Item>
              </Nav>
              <Tab.Content>
                <Tab.Pane eventKey="general">
                  <Row>
                    <Col md={12}>
                      <InputField
                        label="Descrição"
                        name="description"
                        placeholder="Digite a Descrição"
                        register={register}
                        errors={errors}
                        rules={{ required: "Descrição é obrigatória" }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Controller
                        name="productId"
                        control={control}
                        rules={{ required: "Cultura é obrigatório" }}
                        render={({ field, fieldState }) => (
                          <ProductInput
                            value={field.value}
                            onChange={(selectedProduct) => setValue("productId", selectedProduct)}
                            label="Cultura"
                            isInvalid={!!fieldState.error}
                            createdNew={true}
                          />
                        )}
                      />
                    </Col>
                  </Row>
                </Tab.Pane>
                <Tab.Pane eventKey="ardido">{renderDiscountTab("ardido")}</Tab.Pane>
                <Tab.Pane eventKey="avariado">{renderDiscountTab("avariado")}</Tab.Pane>
                <Tab.Pane eventKey="impureza">{renderDiscountTab("impureza")}</Tab.Pane>
                <Tab.Pane eventKey="quebrado">{renderDiscountTab("quebrado")}</Tab.Pane>
                <Tab.Pane eventKey="umidade">{renderDiscountTab("umidade")}</Tab.Pane>
                <Tab.Pane eventKey="verde">{renderDiscountTab("verde")}</Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" className="button-custom w-100 mt-3" type="submit">
              Cadastrar
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ErrorModal
        show={showErrorModal}
        handleClose={handleCloseErrorModal}
        errorMessage={errorMessage}
      />

      <SuccessModal
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
        successMessage={successMessage}
      />
    </>
  );
};

export default CreateDiscountModal;